import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, MessageCircle, ShoppingBag, Gift } from "lucide-react";
import { useState } from "react";
import { api, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { useMembership } from "@/context/MembershipContext";
import { track } from "@/lib/track";
import { optImg } from "@/lib/img";
import { toast } from "sonner";

export const CartDrawer = () => {
  const { items, setQty, removeItem, total, open, setOpen, clear } = useCart();
  const { settings } = useSettings();
  const { member, discountPct, markUsed } = useMembership();
  const [form, setForm] = useState({ name: "", phone: "", address: "", notes: "" });

  const memberDiscount = discountPct > 0 ? Math.round(total * discountPct / 100) : 0;
  const finalTotal = total - memberDiscount;

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const checkout = async () => {
    if (items.length === 0) return;
    if (!form.name.trim() || !form.phone.trim() || !form.address.trim()) {
      toast.error("Completa nombre completo, teléfono y dirección para continuar");
      return;
    }
    const lines = items
      .map((i) => {
        const label = `${i.name}${i.variant ? ` (${i.variant})` : ""}${i.sku ? ` [SKU: ${i.sku}]` : ""}`;
        const disc = i.discount > 0 ? ` (${i.discount}% dto)` : "";
        return `• ${i.qty} × ${label}${disc} — ${formatPrice(i.price * i.qty, settings.currency)}`;
      })
      .join("\n");
    const memberLine = memberDiscount > 0
      ? `\n🎁 Descuento miembro (${discountPct}% primer pedido): -${formatPrice(memberDiscount, settings.currency)}\n*Total a pagar: ${formatPrice(finalTotal, settings.currency)}*`
      : "";
    const msg =
      `¡Hola ${settings.store_name}! 🐾 Quiero realizar este pedido:\n\n${lines}\n\n` +
      `*Subtotal productos: ${formatPrice(total, settings.currency)}*${memberLine}\n\n` +
      `*Mis datos para coordinar la entrega y el pago:*\n` +
      `👤 Nombre completo: ${form.name}\n📞 Teléfono: ${form.phone}\n` +
      `📍 Dirección/Ubicación: ${form.address}\n📝 Notas: ${form.notes || "-"}\n\n` +
      `💳 Pago: al recibir en el GAM (efectivo, SINPE o transferencia).\n` +
      `🚚 Envío fuera del GAM: ₡2.000 (lo coordinamos por aquí).`;

    // record order for customer tracking (non-blocking)
    api.post("/orders", {
      customer_name: form.name,
      phone: form.phone,
      address: form.address,
      notes: form.notes,
      total: finalTotal,
      items: items.map((i) => ({
        product_id: i.productId || "",
        name: i.name,
        variant: i.variant || "",
        sku: i.sku || "",
        qty: i.qty,
        unit_price: i.price,
        original_price: i.original_price || i.price,
        discount: i.discount || 0,
      })),
    }).catch(() => {});

    if (memberDiscount > 0) markUsed();
    track("checkout", { value: finalTotal });
    window.open(`https://wa.me/${settings.whatsapp_number}?text=${encodeURIComponent(msg)}`, "_blank");
    toast.success("¡Pedido enviado a WhatsApp!");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        side="right"
        className="w-full sm:max-w-md p-0 bg-cream border-l border-[#E3E7E2] flex flex-col"
      >
        <div className="px-5 py-4 border-b border-[#E3E7E2] flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-coral" />
          <h2 className="font-display text-xl font-bold text-charcoal">Tu carrito</h2>
          <span className="ml-auto text-sm text-muted-moss">{items.length} producto(s)</span>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {items.length === 0 ? (
            <div className="text-center py-16 text-muted-moss">
              <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="font-semibold">Tu carrito está vacío</p>
              <p className="text-sm">Agrega productos para tu peludo 🐾</p>
            </div>
          ) : (
            items.map((i) => (
              <div
                key={i.id}
                data-testid={`cart-item-${i.id}`}
                className="flex gap-3 bg-white rounded-2xl border border-[#E3E7E2] p-2.5"
              >
                <img src={optImg(i.image_url, 160)} alt={i.name} loading="lazy" decoding="async" className="w-16 h-16 rounded-xl object-cover bg-cream-2" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-charcoal line-clamp-2 leading-snug">{i.name}</p>
                  {(i.variant || i.sku) && (
                    <p className="text-[11px] text-muted-moss mt-0.5">{i.variant}{i.variant && i.sku ? " · " : ""}{i.sku ? `SKU ${i.sku}` : ""}</p>
                  )}
                  <p className="text-sm font-bold text-coral mt-0.5 flex items-baseline gap-1.5">
                    {formatPrice(i.price, settings.currency)}
                    {i.discount > 0 && <span className="text-[11px] font-medium text-muted-moss line-through">{formatPrice(i.original_price, settings.currency)}</span>}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex items-center gap-1 bg-cream rounded-xl border border-[#E3E7E2]">
                      <button onClick={() => setQty(i.id, i.qty - 1)} className="grid place-items-center w-7 h-7 rounded-lg hover:bg-sage/40"><Minus className="w-3.5 h-3.5" /></button>
                      <span className="w-6 text-center text-sm font-bold">{i.qty}</span>
                      <button onClick={() => setQty(i.id, i.qty + 1)} className="grid place-items-center w-7 h-7 rounded-lg hover:bg-sage/40"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                    <button data-testid={`cart-remove-${i.id}`} onClick={() => removeItem(i.id)} className="ml-auto grid place-items-center w-7 h-7 rounded-lg text-muted-moss hover:text-destructive hover:bg-destructive/10"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#E3E7E2] px-5 py-4 space-y-3 bg-white/60">
            {memberDiscount > 0 && (
              <>
                <div className="flex items-center justify-between text-sm text-muted-moss">
                  <span>Subtotal</span>
                  <span>{formatPrice(total, settings.currency)}</span>
                </div>
                <div data-testid="cart-member-discount" className="flex items-center justify-between text-sm font-semibold text-coral">
                  <span className="inline-flex items-center gap-1"><Gift className="w-4 h-4" /> Descuento miembro ({discountPct}%)</span>
                  <span>-{formatPrice(memberDiscount, settings.currency)}</span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-moss">Total</span>
              <span data-testid="cart-total-amount" className="font-display text-2xl font-bold text-charcoal">
                {formatPrice(finalTotal, settings.currency)}
              </span>
            </div>

            {discountPct === 0 && !member && (
              <div className="rounded-xl bg-peach/60 border border-[#E3E7E2] p-2.5 text-xs text-charcoal">
                🎁 <strong>Regístrate</strong> en la página de inicio y obtén <strong>5% de descuento</strong> en tu primer pedido.
              </div>
            )}

            <div className="rounded-xl bg-teal/25 border border-teal/50 p-2.5 text-xs text-charcoal leading-snug space-y-1">
              <p>Al enviar coordinamos <strong>entrega y pago por WhatsApp</strong>. Incluye tu <strong>nombre completo</strong>, <strong>teléfono</strong> y <strong>dirección</strong>: escríbela o (preferible) pega el <strong>link de Waze/Google Maps</strong>, o envíanos tu <strong>ubicación por WhatsApp</strong> en otro mensaje.</p>
              <p>💳 Pago al recibir en el <strong>GAM</strong>: efectivo, SINPE o transferencia · 🚚 Fuera del GAM: <strong>₡2.000</strong> de envío.</p>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <input data-testid="checkout-customer-name" value={form.name} onChange={upd("name")} placeholder="Nombre completo *" className="w-full px-3 py-2.5 rounded-xl border border-[#E3E7E2] bg-cream text-sm outline-none focus:border-coral" />
              <input data-testid="checkout-customer-phone" value={form.phone} onChange={upd("phone")} placeholder="Teléfono *" className="w-full px-3 py-2.5 rounded-xl border border-[#E3E7E2] bg-cream text-sm outline-none focus:border-coral" />
              <textarea data-testid="checkout-customer-address" rows={2} value={form.address} onChange={upd("address")} placeholder="Dirección exacta (o link de Waze/Google Maps) *" className="w-full px-3 py-2.5 rounded-xl border border-[#E3E7E2] bg-cream text-sm outline-none focus:border-coral resize-none" />
              <p className="text-[11px] text-muted-moss -mt-1">Puedes escribir la dirección o pegar el link de Waze/Google Maps (preferido). También puedes enviarnos tu ubicación por WhatsApp en otro mensaje.</p>
              <input value={form.notes} onChange={upd("notes")} placeholder="Notas para la entrega (opcional)" className="w-full px-3 py-2.5 rounded-xl border border-[#E3E7E2] bg-cream text-sm outline-none focus:border-coral" />
            </div>

            <button
              data-testid="checkout-submit-whatsapp"
              onClick={checkout}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-coral text-white font-bold hover:bg-coral-dark active:scale-95 transition-all card-shadow"
            >
              <MessageCircle className="w-5 h-5" /> Finalizar pedido por WhatsApp
            </button>
            <button onClick={clear} className="w-full text-xs text-muted-moss hover:text-destructive">Vaciar carrito</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
